import { useCallback, useState } from "react";
import styles from "./index.module.scss";
import { usePageHeader } from "hooks/usePageHeader";
import { Tabs } from "components/tabs/Tabs";
import { VideoLibraryBroadcastList } from "./VideoLibraryBroadcastList";
import { useTranslation } from "react-i18next";
import { VideoUploadModalEntryPoint } from "components/video-upload/VideoUploadModalEntryPoint";
import { v4 as uuidv4 } from "uuid";
import { Link } from "components/buttons/Link";
import { useGetPagedBroadcastByFilter } from "hooks/useGetPagedBroadcastByFilter";
import { SearchSortOptions } from "components/generic-multiselect/types";
import { useNavigate } from "react-router-dom";
import { BroadcastResponse } from "@switcherstudio/api-core-client";
import { MaintenanceNotice } from "components/maintenance-plan/MaintenanceNotice";

export type VideoLibraryTabs = "on-demand" | "upcoming";

export const VideoLibraryPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    usePageHeader({
        subTitle: (
            <>
                <p>
                    {t("video-library:sub-title")},{" "}
                    <Link
                        to="https://itunes.apple.com/us/app/switcher-studio-pro/id908386221"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t("video-library:sub-title-link")}
                    </Link>
                </p>
                <MaintenanceNotice page="video-library" />
            </>
        )
    });

    const [activeTab, setActiveTab] = useState<VideoLibraryTabs>("on-demand");

    const [uploadSessionId, setUploadSessionId] = useState<string>(uuidv4());
    const [uploadModalKey, setUploadModalKey] = useState<number>(0);

    const {
        getNextPage: onDemandGetNextPage,
        existingBroadcasts,
        unpublishedBroadcasts,
        refetchVideos: onDemandRefetchVideos,
        firstLoading: onDemandFirstLoading,
        fetchBroadcastsWithNewParams: onDemandFetchBroadcastsWithNewParams,
        nextPageLoading: onDemandNextPageLoading,
        refetchFromPageOne: onDemandRefetchFromPageOne,
        allLoaded: onDemandAllLoaded
    } = useGetPagedBroadcastByFilter({
        videoStatusType: "LibraryOnDemand",
        itemsPerPage: 15,
        preFetch: () => {
            setUploadModalKey((prev) => prev + 1);
        }
    });

    const {
        getNextPage: upcomingGetNextPage,
        scheduledBroadcasts,
        refetchVideos: upcomingRefetchVideos,
        firstLoading: upcomingFirstLoading,
        fetchBroadcastsWithNewParams: upcomingFetchBroadcastsWithNewParams,
        nextPageLoading: upcomingNextPageLoading,
        refetchFromPageOne: upcomingRefetchFromPageOne,
        allLoaded: upcomingAllLoaded
    } = useGetPagedBroadcastByFilter({
        videoStatusType: "LibraryUpcoming",
        itemsPerPage: 15,
        preFetch: () => {
            setUploadModalKey((prev) => prev + 1);
        }
    });

    const searchSortOptions: SearchSortOptions = {
        showSearchSort: true,
        location: "video-library"
    };

    const onBroadcastEdit = useCallback(
        (broadcast: BroadcastResponse) => {
            navigate(`/video-library/video/${broadcast.Details.Id}`);
        },
        [navigate]
    );

    return (
        <div className={styles["video-library-page"]}>
            <VideoUploadModalEntryPoint
                sessionId={uploadSessionId}
                setSessionId={setUploadSessionId}
                onUploadComplete={async () => {
                    onDemandRefetchFromPageOne(null);
                    upcomingRefetchFromPageOne(null);
                }}
                refreshKey={uploadModalKey}
                allowMultipleUploads
                isVideoLibrary
                showVideoStorageBar
            />

            <Tabs<VideoLibraryTabs>
                tabs={[
                    {
                        id: "on-demand",
                        label: t("video-library:on-demand"),
                        component: (
                            <VideoLibraryBroadcastList
                                broadcasts={[
                                    ...existingBroadcasts,
                                    ...unpublishedBroadcasts
                                ]}
                                refetchVideos={onDemandRefetchVideos}
                                firstLoading={onDemandFirstLoading}
                                emptyStateCopy={t(
                                    "video-library:no-videos-match"
                                )}
                                searchSortOptions={{
                                    ...searchSortOptions,
                                    refetchWithNewParams: (newParams) => {
                                        onDemandFetchBroadcastsWithNewParams(
                                            newParams
                                        );
                                    }
                                }}
                                onEdit={onBroadcastEdit}
                                showDownload
                                getNextPage={onDemandGetNextPage}
                                nextPageLoading={onDemandNextPageLoading}
                                refetchFromPageOne={onDemandRefetchFromPageOne}
                                allLoaded={onDemandAllLoaded}
                            />
                        )
                    },
                    {
                        id: "upcoming",
                        label: t("video-library:upcoming"),
                        component: (
                            <VideoLibraryBroadcastList
                                broadcasts={scheduledBroadcasts}
                                refetchVideos={upcomingRefetchVideos}
                                firstLoading={upcomingFirstLoading}
                                emptyStateCopy={t(
                                    "video-library:no-videos-match"
                                )}
                                searchSortOptions={{
                                    ...searchSortOptions,
                                    refetchWithNewParams: (newParams) => {
                                        upcomingFetchBroadcastsWithNewParams(
                                            newParams
                                        );
                                    }
                                }}
                                onEdit={onBroadcastEdit}
                                showDownload
                                getNextPage={upcomingGetNextPage}
                                nextPageLoading={upcomingNextPageLoading}
                                refetchFromPageOne={upcomingRefetchFromPageOne}
                                allLoaded={upcomingAllLoaded}
                            />
                        )
                    }
                ]}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
            />
        </div>
    );
};
